import React, { useState } from "react";
import { COMPOSE_EMAIL } from "./ContactUs/mailServices";
import Modal1 from "./Modal/Modal1";
import Lottie from "lottie-react";
import greenTick from "./ContactUs/Lotties/lottie-success.json";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { RingLoader } from "react-spinners";

const BRAND = { navy: "#062b6c", green: "#00ad0f" };

const QuoteComponent = () => {
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [values, setValue] = useState({
    fullname: "",
    email: "",
    phone_number: "",
    message: "",
    country: "",
    companyName: "",
    position: "",
    city: "",
    website: "",
  });

  const handleOnChange = (e) => {
    const { value, id } = e.target;
    setValue((prev) => ({ ...prev, [id]: value }));
  };

  const sendEmail = async (e) => {
    e.preventDefault();

    const { fullname, email, phone_number, message } = values;

    if (!fullname || !email || !phone_number || !message) {
      toast.warn("Please fill all required fields.");
      return;
    }

    setProcessing(true);

    const response = await COMPOSE_EMAIL({
      to: "consult@scaftechservices.com",
      message: `${message} \n 
      Details: \r  
      Name: ${values.fullname} \r 
      Company Name: ${values.companyName} \r
      Position: ${values.position} \r
      Email: ${values.email} \r 
      Mobile: ${values.phone_number}   \r
      Country: ${values.country}  \r
      City: ${values.city}  \r
      Website: ${values.website}  \r
      `,
      subject: `RFQ Quote from: ${fullname} - ${email}`,
    });

    setProcessing(false);

    if (response?.isOk) {
      setSuccess(true);
      navigate(0);
    } else {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <section
      id="get-quote"
      className="relative mx-auto w-full rounded-3xl border border-slate-200 bg-white px-4 py-8 shadow-sm md:px-8 md:py-10"
    >
      {/* top heading strip */}
      <div className="mb-8 flex flex-col gap-4 border-b border-slate-200 pb-5 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-3">
          <span
            className="mt-1 h-10 w-[3px] rounded-full"
            style={{ background: BRAND.navy }}
          />
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-slate-500">
              RFQ • Request For Quote
            </p>
            <h2
              className="mt-1 text-[26px] font-bold leading-[1.15] text-[#062b6c] md:text-[30px]"
              style={{ color: BRAND.navy }}
            >
              Send us a request for quote
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-600 md:text-[0.95rem]">
              Provide your contact information and a brief description of your
              project, equipment needs, or training requirements. We’ll analyse
              your request and respond with a tailored proposal.
            </p>
          </div>
        </div>

        <div className="flex flex-col items-start gap-1 text-[11px] text-slate-500 md:items-end">
          <p>
            <span className="font-semibold text-slate-700">Mail:</span>{" "}
            consult@scaftechservices.com
          </p>
          <p>
            <span className="font-semibold text-slate-700">Phone:</span>{" "}
            +234-816-163-7797
          </p>
        </div>
      </div>

      {/* content grid */}
      <div className="grid gap-8 md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.2fr)]">
        {/* left: quick contact info */}
        <div className="flex flex-col gap-5 rounded-2xl bg-slate-50 p-5 text-sm text-slate-700">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
              Direct Contact
            </p>
            <p className="mt-2 text-[15px] font-semibold text-slate-900">
              Already working with us?
            </p>
            <p className="mt-1 text-[13px] text-slate-600">
              You can also share RFQ details directly with your account manager
              or via our central email and phone line.
            </p>
          </div>

          <div className="space-y-3 text-[13px]">
            <div>
              <p className="font-semibold text-slate-800">Mail</p>
              <a
                className="text-[#062b6c] hover:underline"
                href="mailto:consult@scaftechservices.com"
              >
                consult@scaftechservices.com
              </a>
            </div>
            <div>
              <p className="font-semibold text-slate-800">Phone</p>
              <a
                className="text-[#062b6c] hover:underline"
                href="tel:+2348161637797"
              >
                +234-816-163-7797
              </a>
            </div>
            <div>
              <p className="font-semibold text-slate-800">Office Address</p>
              <p className="text-slate-600">
                Lakowe School Gate, Ibeju-Lekki Lagos, Nigeria.
              </p>
            </div>
          </div>

          <div className="mt-2 rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-[12px] text-slate-600">
            <p className="font-semibold text-slate-800">Helpful RFQ tips:</p>
            <ul className="mt-1 list-disc pl-4">
              <li>Include scope, location, and timelines.</li>
              <li>Mention equipment type or service category.</li>
              <li>
                Attach or describe any existing documentation we should review.
              </li>
            </ul>
          </div>
        </div>

        {/* right: form */}
        <section>
          <form className="max-w-xl" onSubmit={sendEmail} noValidate>
            {/* name + position */}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="relative z-0 mb-5 w-full">
                <input
                  type="text"
                  id="fullname"
                  className="peer block w-full border-0 border-b-2 border-gray-300 bg-transparent px-0 py-2.5 text-sm text-gray-900 focus:border-[#062b6c] focus:outline-none focus:ring-0"
                  placeholder=" "
                  required
                  value={values.fullname}
                  onChange={handleOnChange}
                />
                <label
                  htmlFor="fullname"
                  className="absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 transform text-sm text-gray-500 duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:start-0 peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:font-medium peer-focus:text-[#062b6c]"
                >
                  Full Name
                </label>
              </div>

              <div className="relative z-0 mb-5 w-full">
                <input
                  type="text"
                  id="position"
                  className="peer block w-full border-0 border-b-2 border-gray-300 bg-transparent px-0 py-2.5 text-sm text-gray-900 focus:border-[#062b6c] focus:outline-none focus:ring-0"
                  placeholder=" "
                  required
                  value={values.position}
                  onChange={handleOnChange}
                />
                <label
                  htmlFor="position"
                  className="absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 transform text-sm text-gray-500 duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:start-0 peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:font-medium peer-focus:text-[#062b6c]"
                >
                  Position
                </label>
              </div>
            </div>

            {/* email + phone */}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="relative z-0 mb-5 w-full">
                <input
                  type="email"
                  id="email"
                  className="peer block w-full border-0 border-b-2 border-gray-300 bg-transparent px-0 py-2.5 text-sm text-gray-900 focus:border-[#062b6c] focus:outline-none focus:ring-0"
                  placeholder=" "
                  required
                  value={values.email}
                  onChange={handleOnChange}
                />
                <label
                  htmlFor="email"
                  className="absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 transform text-sm text-gray-500 duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:start-0 peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:font-medium peer-focus:text-[#062b6c]"
                >
                  Email Address
                </label>
              </div>

              <div className="relative z-0 mb-5 w-full">
                <input
                  type="text"
                  id="phone_number"
                  className="peer block w-full border-0 border-b-2 border-gray-300 bg-transparent px-0 py-2.5 text-sm text-gray-900 focus:border-[#062b6c] focus:outline-none focus:ring-0"
                  placeholder=" "
                  required
                  value={values.phone_number}
                  onChange={handleOnChange}
                />
                <label
                  htmlFor="phone_number"
                  className="absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 transform text-sm text-gray-500 duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:start-0 peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:font-medium peer-focus:text-[#062b6c]"
                >
                  Phone Number
                </label>
              </div>
            </div>

            {/* company + country */}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="relative z-0 mb-5 w-full">
                <input
                  type="text"
                  id="companyName"
                  className="peer block w-full border-0 border-b-2 border-gray-300 bg-transparent px-0 py-2.5 text-sm text-gray-900 focus:border-[#062b6c] focus:outline-none focus:ring-0"
                  placeholder=" "
                  required
                  value={values.companyName}
                  onChange={handleOnChange}
                />
                <label
                  htmlFor="companyName"
                  className="absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 transform text-sm text-gray-500 duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:start-0 peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:font-medium peer-focus:text-[#062b6c]"
                >
                  Company Name
                </label>
              </div>

              <div className="relative z-0 mb-5 w-full">
                <input
                  type="text"
                  id="country"
                  className="peer block w-full border-0 border-b-2 border-gray-300 bg-transparent px-0 py-2.5 text-sm text-gray-900 focus:border-[#062b6c] focus:outline-none focus:ring-0"
                  placeholder=" "
                  required
                  value={values.country}
                  onChange={handleOnChange}
                />
                <label
                  htmlFor="country"
                  className="absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 transform text-sm text-gray-500 duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:start-0 peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:font-medium peer-focus:text-[#062b6c]"
                >
                  Country
                </label>
              </div>
            </div>

            {/* website + city */}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="relative z-0 mb-5 w-full">
                <input
                  type="text"
                  id="website"
                  className="peer block w-full border-0 border-b-2 border-gray-300 bg-transparent px-0 py-2.5 text-sm text-gray-900 focus:border-[#062b6c] focus:outline-none focus:ring-0"
                  placeholder=" "
                  value={values.website}
                  onChange={handleOnChange}
                />
                <label
                  htmlFor="website"
                  className="absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 transform text-sm text-gray-500 duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:start-0 peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:font-medium peer-focus:text-[#062b6c]"
                >
                  Website (optional)
                </label>
              </div>

              <div className="relative z-0 mb-5 w-full">
                <input
                  type="text"
                  id="city"
                  className="peer block w-full border-0 border-b-2 border-gray-300 bg-transparent px-0 py-2.5 text-sm text-gray-900 focus:border-[#062b6c] focus:outline-none focus:ring-0"
                  placeholder=" "
                  value={values.city}
                  onChange={handleOnChange}
                />
                <label
                  htmlFor="city"
                  className="absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 transform text-sm text-gray-500 duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:start-0 peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:font-medium peer-focus:text-[#062b6c]"
                >
                  City
                </label>
              </div>
            </div>

            {/* RFQ information */}
            <div className="mb-6">
              <label
                htmlFor="message"
                className="mb-2 block text-sm font-medium text-slate-800"
              >
                RFQ Information
              </label>
              <textarea
                id="message"
                rows="4"
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-[#062b6c] focus:ring-1 focus:ring-[#062b6c]"
                placeholder="Describe your project, required services, timelines, and any specific standards or constraints."
                value={values.message}
                onChange={handleOnChange}
                required
              />
            </div>

            <button
              type="submit"
              disabled={processing}
              className="my-4 inline-flex items-center justify-center rounded-full bg-[#062b6c] px-6 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-[#03112f] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {processing ? <RingLoader color="#fff" size={18} /> : "Submit RFQ"}
            </button>
          </form>
        </section>
      </div>

      <Modal1 visible={success} onClose={() => setSuccess(false)}>
        <div className="flex min-h-[260px] flex-col items-center justify-center">
          <Lottie className="w-[100px]" animationData={greenTick} />
          <p className="mt-4 text-2xl font-bold text-[#062b6c]">
            Message sent
          </p>
          <p className="mt-1 px-6 text-center text-sm text-slate-600">
            Your RFQ was sent successfully. Our team will get back to you
            shortly.
          </p>
        </div>
      </Modal1>

      <ToastContainer />
    </section>
  );
};

export default QuoteComponent;
